import { useEffect, useMemo, useRef, useState } from 'react'
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded'
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import KeyRoundedIcon from '@mui/icons-material/KeyRounded'
import { collection, doc, onSnapshot, query, updateDoc, where, type QueryDocumentSnapshot } from 'firebase/firestore'
import { firestoreDb } from '../../../services/firebase'
import { endWorkspaceMember, leaveWorkspaceAndPurgeOutbox } from '../../../services/memberService'
import { useToast } from '../../../context/useToast'
import { useAuth } from '../../../context/useAuth'
import { useWorkspace } from '../../../context/useWorkspace'
import { requestWorkspaceSwitch } from '../../../services/workspaceSwitchService'
import { buildMemberPath } from '../../../member/memberAppRouting'
import { safeMemberLanding } from '../../../services/workspaceSwitch/switchLanding'
import { useNls } from '../../../nls/nlsProvider'
import type { NlsKey } from '../../../nls/strings_nls'
import { isWorkspaceOwner } from '../../../utils/workspaceOwners'
import { isNoSignInPreviewActive } from '../../../debug'
import { DEBUG_ADMIN_COUNTERPARTY, DEBUG_EDITOR_USER, DEBUG_HELPER_USER, DEBUG_VIEWER_USER } from '../../../debug/fixtures'
import { isWorkspaceRole, normalizeBoardWorkspaceRole } from '../../../utils/workspacePermissions'
import type { Workspace, WorkspaceRole } from '../../../types/workspace'
import type { MemberControls, MemberControlsSettings } from '../../../types/memberControls'
import { roleLabelKey } from '../../../utils/roleLabels'
import { TIMELINE_BOARD_KEYS, type TimelineBoardKey } from '../../../types/schema'
import {
  InvitesHubV2,
  type InvitesHubV2Handle,
} from '../../InvitesHubV2'
import {
  isCategoryAllowed,
  TM12_CATEGORIES,
  type CategoryKey,
} from '../../Invites/invitesCategoryModel'
import type { WorkspacePermissionManagerSaveHandle } from '../../Admin/WorkspacePermissionManager'
import { MemberBottomSheet, MemberEmptyState, MemberHeroHeader, MemberSegmented } from './MemberControlsPrimitives'
import { MemberControlsSheet } from './MemberControlsSheet'
import { ControlProfilesManager } from './ControlProfilesManager'
import { AccessScopePanel } from '../../Members/AccessScopePanel'
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { Button } from '../../Common/Button'
import { Checkbox } from '../../Common/Checkbox'
import { Pressable } from '../../Common/Pressable'
import { Fab } from '../../Common/Fab'
import { Avatar } from '../../Common/Avatar'
import { LeaveWorkspaceConfirmModal } from '../../Common/LeaveWorkspaceConfirmModal'
import { MobileFullScreenPanel } from '../../Common/MobileFullScreenPanel'
import { SeatsStrip } from '../../Common/SeatsStrip'
import { WorkspaceTabBarDefaultsPanel } from '../../Admin/panels/WorkspaceTabBarDefaultsPanel'
import { MemberBulkControlsBar } from './MemberBulkControlsBar'
import './MemberPeopleMobileRevamp.css'

type Presence = 'ONLINE' | 'AWAY' | 'OFFLINE'
type Sort = 'RECENT' | 'NAME' | 'ROLE' | 'JOINED'
type RosterPerson = { id: string; uid: string | null; name: string; email: string; role: WorkspaceRole; boardRoles: Partial<Record<TimelineBoardKey, WorkspaceRole>>; credits: number; dailyCreditAllowance: number; creditsConsumedToday: number; lastActiveMs: number | null; joinedAtMs: number | null; photoURL: string | null; controls?: MemberControlsSettings | null; effectiveControls?: MemberControls | null }

const toMillis = (value: unknown): number | null => value && typeof (value as { toMillis?: () => number }).toMillis === 'function' ? (value as { toMillis: () => number }).toMillis() : null
const mapRosterPerson = (snapshot: QueryDocumentSnapshot): RosterPerson | null => {
  const data = snapshot.data()
  if (data.status && data.status !== 'ACTIVE') return null
  const role = isWorkspaceRole(data.role) ? data.role : 'VIEWER'
  const uid = typeof data.uid === 'string' && data.uid ? data.uid : typeof data.userId === 'string' && data.userId ? data.userId : null
  const email = typeof data.email === 'string' ? data.email : typeof data.user_email === 'string' ? data.user_email : ''
  const preferences = data.displayPreferences && typeof data.displayPreferences === 'object' ? data.displayPreferences as Record<string, unknown> : null
  const name = typeof preferences?.computedName === 'string' && preferences.computedName ? preferences.computedName : typeof data.displayName === 'string' && data.displayName ? data.displayName : typeof data.name === 'string' && data.name ? data.name : email || 'Workspace member'
  const normalizedCount = (value: unknown) => typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0
  const boardRoles = data.boardRoles && typeof data.boardRoles === 'object'
    ? Object.fromEntries(Object.entries(data.boardRoles as Record<string, unknown>).filter(([board]) => TIMELINE_BOARD_KEYS.includes(board as TimelineBoardKey)).map(([board, boardRole]) => [board, normalizeBoardWorkspaceRole(boardRole)])) as Partial<Record<TimelineBoardKey, WorkspaceRole>>
    : {}
  return { id: snapshot.id, uid, name, email, role, boardRoles, credits: normalizedCount(data.credits), dailyCreditAllowance: normalizedCount(data.dailyCreditAllowance), creditsConsumedToday: normalizedCount(data.creditsConsumedToday), lastActiveMs: toMillis(data.lastReadAt) ?? toMillis(data.lastActiveAt) ?? toMillis(data.updatedAt) ?? toMillis(data.joinedAt), joinedAtMs: toMillis(data.joinedAt), photoURL: typeof data.photoURL === 'string' ? data.photoURL : null, controls: (data.controls as MemberControlsSettings) ?? null, effectiveControls: (data.effectiveControls as MemberControls) ?? null }
}
const debugRoster = (): RosterPerson[] => [
  { id: 'debug-admin-counterparty', uid: DEBUG_ADMIN_COUNTERPARTY.uid, name: DEBUG_ADMIN_COUNTERPARTY.displayName, email: DEBUG_ADMIN_COUNTERPARTY.email, role: 'ADMIN', boardRoles: {}, credits: 0, dailyCreditAllowance: 0, creditsConsumedToday: 0, lastActiveMs: Date.now() - 12 * 60_000, joinedAtMs: Date.now() - 8 * 86400_000, photoURL: null, controls: null, effectiveControls: null },
  { id: 'debug-editor', uid: DEBUG_EDITOR_USER.uid, name: DEBUG_EDITOR_USER.displayName ?? 'Debug Editor', email: DEBUG_EDITOR_USER.email ?? '', role: 'EDITOR', boardRoles: {}, credits: 0, dailyCreditAllowance: 0, creditsConsumedToday: 0, lastActiveMs: Date.now() - 45 * 60_000, joinedAtMs: Date.now() - 5 * 86400_000, photoURL: null, controls: null, effectiveControls: null },
  { id: 'debug-viewer', uid: DEBUG_VIEWER_USER.uid, name: DEBUG_VIEWER_USER.displayName ?? 'Debug Viewer', email: DEBUG_VIEWER_USER.email ?? '', role: 'VIEWER', boardRoles: {}, credits: 0, dailyCreditAllowance: 0, creditsConsumedToday: 0, lastActiveMs: Date.now() - 2 * 60 * 60_000, joinedAtMs: Date.now() - 3 * 86400_000, photoURL: null, controls: null, effectiveControls: null },
  { id: 'debug-helper', uid: DEBUG_HELPER_USER.uid, name: DEBUG_HELPER_USER.displayName ?? 'Debug Helper', email: DEBUG_HELPER_USER.email ?? '', role: 'HELPER', boardRoles: {}, credits: 0, dailyCreditAllowance: 0, creditsConsumedToday: 0, lastActiveMs: Date.now() - 3 * 60_000, joinedAtMs: Date.now() - 86400_000, photoURL: null, controls: null, effectiveControls: null },
]
const presenceFor = (person: RosterPerson): Presence => !person.lastActiveMs ? 'OFFLINE' : Date.now() - person.lastActiveMs < 5 * 60_000 ? 'ONLINE' : Date.now() - person.lastActiveMs < 30 * 60_000 ? 'AWAY' : 'OFFLINE'
const relativeTime = (ms: number | null, t: (key: NlsKey) => string) => {
  if (!ms) return t('NLSTeamRelativeNoActivity')
  const minutes = Math.max(0, Math.round((Date.now() - ms) / 60_000))
  if (minutes < 1) return t('NLSTeamRelativeJustNow')
  if (minutes < 60) return t('NLSTeamRelativeMinutesAgo').replace('{minutes}', String(minutes))
  if (minutes < 1440) return t('NLSTeamRelativeHoursAgo').replace('{hours}', String(Math.round(minutes / 60)))
  return t('NLSTeamRelativeDaysAgo').replace('{days}', String(Math.round(minutes / 1440)))
}
const initialsFor = (person: RosterPerson) => person.name.trim().split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase() || 'M'

export const MemberPeopleMobile = ({ workspace }: { workspace: Workspace }) => {
  const { t } = useNls()
  const { showToast } = useToast()
  const { currentUser } = useAuth()
  const { canPerform, activeRole, workspaces, refreshWorkspaces } = useWorkspace()
  const isOwnerViewer = isWorkspaceOwner(workspace, currentUser?.uid)
  const invitesRef = useRef<InvitesHubV2Handle | null>(null)
  const permissionSaveHandle = useRef<WorkspacePermissionManagerSaveHandle | null>(null)
  const [leavingWorkspace, setLeavingWorkspace] = useState<{ id: string; name: string } | null>(null)

  const handleLeftWorkspace = async () => {
    if (!leavingWorkspace) return
    const leftId = leavingWorkspace.id
    setLeavingWorkspace(null)
    setSelected(null)
    if (refreshWorkspaces) await refreshWorkspaces()
    const remaining = workspaces.filter((entry) => entry.id !== leftId && !entry.isPreview)
    if (remaining.length > 0) {
      void requestWorkspaceSwitch({
        toWorkspaceId: remaining[0].id,
        source: 'MEMBERSHIP_ENDED',
        landing: safeMemberLanding({ workspace: remaining[0], tab: 'for-you' }),
      })
    } else {
      void requestWorkspaceSwitch({
        toWorkspaceId: null,
        source: 'MEMBERSHIP_ENDED',
      })
    }
  }
  const canManageMembers = canPerform('workspace.manage_members')
  const canManageInvites = canPerform('workspace.manage_invites')
  const canManageJoinCodes = isOwnerViewer || activeRole === 'ADMIN'
  const canViewPermissionsRail = canPerform('workspace.manage_permissions')
  const canManageFieldPasses = canPerform('workspace.manage_field_passes')
  const canEditSettings = canPerform('workspace.edit_settings')

  const allowedSegments = useMemo(() => {
    return TM12_CATEGORIES.filter((key) =>
      isCategoryAllowed(key, {
        canManageInvites,
        canManageJoinCodes,
        canViewPermissions: canViewPermissionsRail,
        canManageFieldPasses,
      })
    )
  }, [canManageInvites, canManageJoinCodes, canViewPermissionsRail, canManageFieldPasses])

  const [segment, setSegment] = useState<CategoryKey>('MEMBERS')

  if (!allowedSegments.includes(segment)) {
    setSegment(allowedSegments[0] ?? 'MEMBERS')
  }

  const [roster, setRoster] = useState<RosterPerson[]>(() => isNoSignInPreviewActive() ? debugRoster() : [])
  const [selected, setSelected] = useState<RosterPerson | null>(null)
  const [filterOpen, setFilterOpen] = useState(false)
  const [roles, setRoles] = useState<WorkspaceRole[]>([])
  const [presence, setPresence] = useState<Presence[]>([])
  const [sort, setSort] = useState<Sort>('RECENT')
  const [permissionsDirty, setPermissionsDirty] = useState(false)
  const [memberActionBusy, setMemberActionBusy] = useState<string | null>(null)
  const [phoneTabBarOpen, setPhoneTabBarOpen] = useState(false)
  const [profilesManagerOpen, setProfilesManagerOpen] = useState(false)
  const [isSelectMode, setIsSelectMode] = useState(false)
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(() => new Set())
  useEffect(() => {
    if (isNoSignInPreviewActive()) return undefined
    return onSnapshot(query(collection(firestoreDb, 'workspace_members'), where('workspaceId', '==', workspace.id)), (snapshot) => setRoster(snapshot.docs.map(mapRosterPerson).filter((person): person is RosterPerson => person !== null)), (error) => showToast(error.message, 'error'))
  }, [showToast, workspace.id])
  const filteredRoster = useMemo(() => roster.filter((person) => roles.length === 0 || roles.includes(person.role)).filter((person) => presence.length === 0 || presence.includes(presenceFor(person))).sort((left, right) => sort === 'NAME' ? left.name.localeCompare(right.name) : sort === 'ROLE' ? left.role.localeCompare(right.role) || left.name.localeCompare(right.name) : sort === 'JOINED' ? (right.joinedAtMs ?? 0) - (left.joinedAtMs ?? 0) : (right.lastActiveMs ?? 0) - (left.lastActiveMs ?? 0)), [presence, roles, roster, sort])
  const activeFilterCount = roles.length + presence.length + (sort === 'RECENT' ? 0 : 1)
  const toggleRole = (role: WorkspaceRole) => setRoles((current) => current.includes(role) ? current.filter((entry) => entry !== role) : [...current, role])
  const togglePresence = (value: Presence) => setPresence((current) => current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value])
  const clearFilters = () => { setRoles([]); setPresence([]); setSort('RECENT') }
  const remove = async (person: RosterPerson) => {
    if (!canManageMembers || person.role === 'OWNER' || (person.role === 'ADMIN' && !isOwnerViewer)) return
    try {
      if (isNoSignInPreviewActive()) setRoster((current) => current.filter((entry) => entry.id !== person.id))
      else await endWorkspaceMember({ workspaceId: workspace.id, targetUserId: person.uid || person.id })
      setSelected(null)
      showToast('Member removed.', 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not remove member.', 'error')
    }
  }
  const message = (person: RosterPerson) => { window.history.pushState(null, '', buildMemberPath({ workspace, tab: 'nexus' })); window.dispatchEvent(new PopStateEvent('popstate')); showToast(`Opened chat for ${person.name}.`, 'ghost') }
  const updateMember = async (person: RosterPerson, action: string, patch: Partial<RosterPerson>, success: string) => {
    if (!canManageMembers) return
    if (person.role === 'OWNER' || patch.role === 'OWNER') return
    if (person.role === 'ADMIN' && !isOwnerViewer && (patch.role || patch.boardRoles)) return
    setMemberActionBusy(action)
    try {
      if (isNoSignInPreviewActive()) {
        setRoster((current) => current.map((entry) => entry.id === person.id ? { ...entry, ...patch } : entry))
        setSelected((current) => current?.id === person.id ? { ...current, ...patch } : current)
      } else {
        await updateDoc(doc(firestoreDb, 'workspace_members', person.id), patch)
      }
      showToast(success, 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : `Could not ${success.toLowerCase()}`, 'error')
    } finally {
      setMemberActionBusy(null)
    }
  }

  const segmentOptions = useMemo(() => {
    const labels: Record<CategoryKey, string> = {
      MEMBERS: t('NLSTeamSectionPeople'),
      JOIN_CODES: t('NLSTeamSectionJoinCodes'),
      INVITES: t('NLSTeamSectionInvites'),
      HELPERS: t('NLSTeamSectionHelpers'),
      FIELD_PASSES: t('NLSTeamSectionFieldPasses'),
      PERMISSIONS: t('NLSTeamSectionPermissions'),
      GENERATE: t('NLSTeamSectionInvites'),
    }
    return allowedSegments.map((key) => ({
      key,
      label: labels[key],
      count: key === 'MEMBERS' ? roster.length : undefined,
    }))
  }, [allowedSegments, roster.length, t])

  return <section className="memberControlsMobile memberPeopleMobile memberPeopleMobile--revamp" data-testid="member-people-mobile">
    <SeatsStrip workspaceId={workspace.id} />
    <MemberHeroHeader eyebrow={t('NLSMemberPeopleEyebrow')} title={t('NLSMemberPeopleTitle')} />
    <MemberSegmented<CategoryKey>
      value={segment}
      onChange={(next) => {
        setSegment(next)
        setIsSelectMode(false)
        setSelectedMemberIds(new Set())
      }}
      ariaLabel={t('NLSTeamFilterRosterTitle')}
      options={segmentOptions}
    />
    {segment === 'PERMISSIONS' && canEditSettings ? (
      <div className="memberPeopleMobile__settingsSection">
        <Pressable
          className="memberPeopleMobile__row"
          onClick={() => setPhoneTabBarOpen(true)}
          data-testid="member-phone-tab-bar-row"
        >
          <PhoneIphoneRoundedIcon className="memberPeopleMobile__phoneIcon" />
          <span className="memberPeopleMobile__identity">
            <strong>{t('NLSWorkspaceTabBarDefaultsTitle')}</strong>
            <small>{t('NLSWorkspaceTabBarDefaultsSubtitle')}</small>
          </span>
          <ChevronRightRoundedIcon className="memberPeopleMobile__chevronIcon" />
        </Pressable>
      </div>
    ) : null}
    {segment === 'MEMBERS' ? (
      <div className="memberPeopleRoster memberPeopleMobile__roster">
        {canManageMembers ? (
          <div className="memberPeopleMobile__profilesBar mb-2 flex justify-end gap-2 items-center">
            {!isSelectMode ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsSelectMode(true)}
                  data-testid="mobile-select-mode-btn"
                >
                  {t('NLSControlsSelectMode')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setProfilesManagerOpen(true)}
                >
                  {t('NLSControlsProfilesAction')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setSelectedMemberIds(new Set(filteredRoster.map((p) => p.id)))
                  }}
                  data-testid="mobile-select-all-btn"
                >
                  {t('NLSControlsSelectAllShown')}
                </Button>
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => {
                    setIsSelectMode(false)
                    setSelectedMemberIds(new Set())
                  }}
                  data-testid="mobile-cancel-select-btn"
                >
                  {t('NLSConfirmActionCancel')}
                </Button>
              </>
            )}
          </div>
        ) : null}
        {activeFilterCount > 0 ? (
          <div className="memberPeopleMobile__filterSummary">
            <span>{roles.join(', ')}</span>
            <span>
              {presence
                .map((p) =>
                  p === 'ONLINE'
                    ? t('NLSPresenceOnline')
                    : p === 'AWAY'
                      ? t('NLSPresenceAway')
                      : t('NLSPresenceOffline')
                )
                .join(', ')}
            </span>
            <span>
              {t('NLSTeamFilterSortBy')}:{' '}
              {sort === 'RECENT'
                ? t('NLSTeamSortRecentlyActive')
                : sort === 'NAME'
                  ? t('NLSTeamSortName')
                  : sort === 'ROLE'
                    ? t('NLSTeamSortRole')
                    : t('NLSTeamSortRecentlyAdded')}
            </span>
            <Button variant="link" size="sm" onClick={clearFilters}>
              {t('NLSTeamFilterClear')}
            </Button>
          </div>
        ) : null}
        {filteredRoster.length === 0 ? (
          <MemberEmptyState
            icon={<GroupsRoundedIcon fontSize="inherit" />}
            title={roster.length ? t('NLSTeamEmptyFilterTitle') : t('NLSTeamEmptyNoMembersTitle')}
            hint={roster.length ? t('NLSTeamEmptyFilterHint') : t('NLSTeamEmptyNoMembersHint')}
            cta={
              roster.length ? (
                <Button variant="secondary" size="md" onClick={clearFilters}>
                  {t('NLSTeamClearFilters')}
                </Button>
              ) : null
            }
          />
        ) : (
          filteredRoster.map((person) => {
            const duplicateEmail = person.name.trim().toLowerCase() === person.email.trim().toLowerCase()
            const status = presenceFor(person)
            const isSelected = selectedMemberIds.has(person.id)
            return (
              <div key={person.id} className="flex items-center gap-2">
                {isSelectMode ? (
                  <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onChange={(checked) => {
                        setSelectedMemberIds((prev) => {
                          const next = new Set(prev)
                          if (checked) next.add(person.id)
                          else next.delete(person.id)
                          return next
                        })
                      }}
                      aria-label={`Select ${person.name}`}
                      data-testid={`select-member-${person.id}`}
                    />
                  </div>
                ) : null}
                <div className="flex-1 min-w-0">
                  <Pressable
                    className="memberPeopleMobile__row"
                    onClick={() => {
                      if (isSelectMode) {
                        setSelectedMemberIds((prev) => {
                          const next = new Set(prev)
                          if (next.has(person.id)) next.delete(person.id)
                          else next.add(person.id)
                          return next
                        })
                      } else {
                        setSelected(person)
                      }
                    }}
                  >
                    <Avatar
                      photoURL={person.photoURL}
                      name={person.name}
                      initials={initialsFor(person)}
                      className={`memberPeopleMobile__avatar is-${status.toLowerCase()}`}
                    />
                    <span className="memberPeopleMobile__identity">
                      <strong>{person.name}</strong>
                      {person.email && !duplicateEmail ? <small>{person.email}</small> : null}
                      <small>{relativeTime(person.lastActiveMs, t)}</small>
                    </span>
                    <span className="memberPeopleMobile__roles">
                      <em>{t(roleLabelKey(person.role))}</em>
                      <small>
                        {status === 'ONLINE'
                          ? t('NLSPresenceOnline')
                          : status === 'AWAY'
                            ? t('NLSPresenceAway')
                            : t('NLSPresenceOffline')}
                      </small>
                    </span>
                  </Pressable>
                </div>
              </div>
            )
          })
        )}
        {isSelectMode && selectedMemberIds.size > 0 ? (
          <MemberBulkControlsBar
            workspaceId={workspace.id}
            selectedMembers={roster
              .filter((p) => selectedMemberIds.has(p.id))
              .map((p) => ({
                id: p.id,
                uid: p.uid,
                role: p.role,
                displayName: p.name,
              }))}
            isOwnerViewer={isOwnerViewer}
            onClearSelection={() => {
              setSelectedMemberIds(new Set())
              setIsSelectMode(false)
            }}
          />
        ) : null}
      </div>
    ) : (
      <div className="memberInvitationsFrame memberPeopleMobile__rail">
        {segment === 'FIELD_PASSES' ? <header className="memberPeopleMobile__subhead"><span className="memberEyebrow">{t('NLSTeamSectionFieldPasses')}</span><p>{t('NLSTeamFieldPassSubhead')}</p></header> : null}
        <InvitesHubV2 ref={invitesRef} isMobile mobileRail={segment} mobileHideOwnHeader onPermissionDirtyChange={segment === 'PERMISSIONS' ? setPermissionsDirty : undefined} permissionSaveHandle={segment === 'PERMISSIONS' ? permissionSaveHandle : undefined} />
      </div>
    )}
    {segment === 'PERMISSIONS' ? <footer className="memberPeopleMobile__saveFooter"><Button variant="primary" size="lg" disabled={!permissionsDirty} onClick={() => void permissionSaveHandle.current?.save()}>{t('NLSTeamSavePermissions')}</Button></footer> : null}
    {segment === 'INVITES' ? <Fab extended className="memberGradientFab" onClick={() => invitesRef.current?.openCreatePanel('MEMBER')} icon={<GroupAddRoundedIcon fontSize="small" />} label={t('NLSTeamInviteFab')} /> : null}
    {segment === 'HELPERS' ? <Fab extended className="memberGradientFab" onClick={() => invitesRef.current?.openCreatePanel('HELPER')} icon={<GroupAddRoundedIcon fontSize="small" />} label={t('NLSTeamInviteFab')} /> : null}
    {segment === 'FIELD_PASSES' ? <Fab extended className="memberGradientFab" onClick={() => invitesRef.current?.openNewFieldPass()} icon={<KeyRoundedIcon fontSize="small" />} label={t('NLSTeamCreateFieldPassFab')} /> : null}
    {segment === 'MEMBERS' ? <Fab extended className="memberGradientFab" onClick={() => setFilterOpen(true)} icon={<FilterListRoundedIcon fontSize="small" />} label={t('NLSTeamFilterFab')} children={<>{t('NLSTeamFilterFab')}{activeFilterCount ? <span className="memberPeopleMobile__filterBadge">{activeFilterCount}</span> : null}</>} /> : null}
    <MemberBottomSheet open={filterOpen} onClose={() => setFilterOpen(false)} title={t('NLSTeamFilterRosterTitle')} testId="member-roster-filter-sheet"><section className="memberPeopleMobile__filterSheet"><header><strong>{t('NLSTeamFilterRosterTitle')}</strong><Button variant="link" size="sm" disabled={!activeFilterCount} onClick={clearFilters}>{t('NLSTeamFilterReset')}</Button></header><div><span>{t('NLSTeamFilterRole')}</span>{(['ADMIN', 'EDITOR', 'VIEWER', 'READONLY', 'HELPER'] as WorkspaceRole[]).map((role) => <Pressable key={role} aria-pressed={roles.includes(role)} className={roles.includes(role) ? 'isActive' : ''} onClick={() => toggleRole(role)}>{role}</Pressable>)}</div><div><span>{t('NLSTeamFilterStatus')}</span>{([['ONLINE', t('NLSPresenceOnline')], ['AWAY', t('NLSPresenceAway')], ['OFFLINE', t('NLSPresenceOffline')]] as [Presence, string][]).map(([value, label]) => <Pressable key={value} aria-pressed={presence.includes(value)} className={presence.includes(value) ? 'isActive' : ''} onClick={() => togglePresence(value)}>{label}</Pressable>)}</div><div><span>{t('NLSTeamFilterSortBy')}</span>{([['RECENT', t('NLSTeamSortRecentlyActive')], ['NAME', t('NLSTeamSortName')], ['ROLE', t('NLSTeamSortRole')], ['JOINED', t('NLSTeamSortRecentlyAdded')]] as [Sort, string][]).map(([value, label]) => <Pressable key={value} aria-pressed={sort === value} className={sort === value ? 'isActive' : ''} onClick={() => setSort(value)}>{label}</Pressable>)}</div><Button variant="primary" size="lg" className="memberPeopleMobile__showResults" disabled={!filteredRoster.length} onClick={() => setFilterOpen(false)}>{filteredRoster.length ? t('NLSTeamShowFilteredMembers').replace('{count}', String(filteredRoster.length)) : t('NLSTeamFilterNoMatches')}</Button></section></MemberBottomSheet>
    <MemberBottomSheet open={Boolean(selected)} onClose={() => setSelected(null)} title={selected?.name ?? t('NLSTeamDefaultMemberTitle')} testId="member-roster-sheet">{selected ? <MemberControlsSheet
      member={{ id: selected.id, uid: selected.uid, email: selected.email, displayName: selected.name, role: selected.role, boardRoles: selected.boardRoles, credits: selected.credits, dailyCreditAllowance: selected.dailyCreditAllowance, creditsConsumedToday: selected.creditsConsumedToday, lastActiveMs: selected.lastActiveMs, photoURL: selected.photoURL, controls: selected.controls, effectiveControls: selected.effectiveControls }}
      workspaceId={workspace.id}
      kind={selected.role === 'HELPER' ? 'HELPER' : 'MEMBER'}
      mobile
      canManageMembers={canManageMembers}
      isOwnerViewer={isOwnerViewer}
      accessControls={
        selected.uid && canManageMembers ? (
          <AccessScopePanel
            workspaceId={workspace.id}
            member={{ uid: selected.uid, displayName: selected.name, role: selected.role }}
            canManage={canManageMembers}
          />
        ) : undefined
      }
      busyAction={memberActionBusy}
      onMessage={() => message(selected)}
      onRemove={() => {
        if (selected.uid === currentUser?.uid) {
          setLeavingWorkspace({ id: workspace.id, name: workspace.name })
        } else {
          void remove(selected)
        }
      }}
      removeLabel={
        selected.uid === currentUser?.uid
          ? t('NLSInvitesLeaveWorkspaceAction')
          : undefined
      }
      removeDisabled={
        selected.uid === currentUser?.uid
          ? false
          : selected.role === 'OWNER' ||
            (selected.role === 'ADMIN' && !isOwnerViewer)
      }
      removeTitle={
        selected.uid === currentUser?.uid
          ? undefined
          : selected.role === 'OWNER'
            ? t('NLSInvitesRemoveOwnerTooltip')
            : (selected.role === 'ADMIN' && !isOwnerViewer)
              ? t('NLSInvitesAdminOwnerOnlyTooltip')
              : undefined
      }
      onRoleChange={(role) => {
        if (selected.role === 'OWNER' || role === 'OWNER') return
        if (selected.role === 'ADMIN' && !isOwnerViewer) return
        void updateMember(selected, `role:${selected.id}`, { role }, 'Role updated.')
      }}
      onBoardPermissionChange={(board, role) => {
        if (selected.role === 'OWNER' || role === 'OWNER') return
        if (selected.role === 'ADMIN' && !isOwnerViewer) return
        void updateMember(selected, `board:${selected.id}:${board}`, { boardRoles: { ...selected.boardRoles, [board]: role } }, 'Board permission updated.')
      }}
      onCreditAllot={(value) => { const delta = Number.parseInt(value, 10); if (Number.isFinite(delta) && delta !== 0) void updateMember(selected, `credits:${selected.id}`, { credits: Math.max(0, selected.credits + delta) }, `${delta > 0 ? 'Allotted' : 'Reclaimed'} ${Math.abs(delta)} credit${Math.abs(delta) === 1 ? '' : 's'}.`) }}
    /> : null}</MemberBottomSheet>
    {phoneTabBarOpen && (
      <MobileFullScreenPanel
        title={t('NLSWorkspaceTabBarDefaultsTitle')}
        onClose={() => setPhoneTabBarOpen(false)}
        closeLabel={t('NLSCommonActionClose')}
      >
        <div className="memberPeopleMobile__panelContent">
          <WorkspaceTabBarDefaultsPanel
            workspaceId={workspace.id}
            workspace={workspace}
            defaults={workspace.tabDefaults}
            canEdit={canEditSettings}
            isMobile
            onSaved={() => setPhoneTabBarOpen(false)}
          />
        </div>
      </MobileFullScreenPanel>
    )}
    <ControlProfilesManager
      workspaceId={workspace.id}
      members={roster}
      isOpen={profilesManagerOpen}
      onClose={() => setProfilesManagerOpen(false)}
      canManage={canManageMembers}
    />
    {leavingWorkspace ? (
      <LeaveWorkspaceConfirmModal
        open={Boolean(leavingWorkspace)}
        workspaceId={leavingWorkspace.id}
        workspaceName={leavingWorkspace.name}
        onClose={() => setLeavingWorkspace(null)}
        onConfirm={async () => {
          await leaveWorkspaceAndPurgeOutbox({ workspaceId: leavingWorkspace.id })
        }}
        onLeft={handleLeftWorkspace}
        onNavigateToOwners={() => setLeavingWorkspace(null)}
      />
    ) : null}
  </section>
}
